import { Flex, SkeletonText, Text } from '@chakra-ui/react'
import { ChevronLeft, EllipsisVertical, Link, Maximize } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Close } from '~/components/compositional/QuickActions'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownActionButton,
  MangoButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownActionButton'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { Size } from '~/types/ThemeTypes'

import { formatBasicDateTimeString } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { CallInstance } from '@mangovoice/types'

interface CallDetailsViewHeaderProps {
  callInstanceLoading: boolean
  callInstance?: CallInstance
  isModal: boolean
  onClosed?: () => void
  handleCopyLink: (copyLinkAtTimestamp?: boolean) => void
  onOpenModal?: () => void
  shouldBeClosed?: boolean
  onDownloadAudio?: () => void
}

export const CallDetailsViewHeader = ({
  callInstanceLoading,
  callInstance,
  isModal,
  onClosed,
  handleCopyLink,
  onOpenModal,
  onDownloadAudio,
}: CallDetailsViewHeaderProps) => {
  const { t } = useTranslation()
  const dropdownItems: MangoButtonMenuItem<string>[] = [
    {
      value: 'copy-link',
      content: t('callDetails.header.copyLinkToCall'),
      onSelect: () => handleCopyLink(),
    },
    {
      value: 'copy-link-at-timestamp',
      content: t('callDetails.header.copyLinkToCallAtTimestamp'),
      onSelect: () => handleCopyLink(true),
    },
    {
      value: 'download-audio',
      content: t('callDetails.header.downloadAudio'),
      onSelect: () => onDownloadAudio?.(),
    },
  ]

  const hasAudio = !!callInstance?.recording_url

  return (
    <Flex
      data-testid="call-logs-detail-view-header"
      height="4.5rem"
      py="1.25rem"
      px="1.25rem"
      justifyContent="space-between"
      alignItems="center"
      alignSelf="stretch"
      bg="bg.panel"
      borderBottom="2px solid"
      borderColor="border.muted"
      flex="0 1 auto"
    >
      <Flex alignItems="center">
        {!isModal && onClosed && (
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_100}
            tooltipText={t('callDetails.header.closePanel')}
            actionIcon={ChevronLeft}
            onClick={onClosed}
            mr="0.75rem"
          />
        )}
        <Flex
          data-testid="call-logs-detail-view-header-title"
          direction="column"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Text {...textStyles[textStyleKeys.H4]}>
            {t('callDetails.title')}
          </Text>
          {callInstanceLoading ? (
            <SkeletonText
              lineClamp={1}
              py="0.125rem"
              width="15rem"
              height="1rem"
            />
          ) : (
            <Text
              {...textStyles[textStyleKeys.Body2]}
              color="var(--chakra-colors-fg-muted)"
            >
              {t(`callDetailData.direction.${callInstance!.direction}`)}{' '}
              {t('callDetails.callOn')}{' '}
              {formatBasicDateTimeString(callInstance!.started_at)}
            </Text>
          )}
        </Flex>
      </Flex>
      {!isModal && !callInstanceLoading && onOpenModal && (
        <Flex
          data-testid="call-logs-detail-view-header-quickactions"
          alignItems="center"
          gap="0.75rem"
        >
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_200}
            tooltipText={t('callDetails.header.expand')}
            actionIcon={Maximize}
            onClick={onOpenModal}
          />
          {hasAudio ? (
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText={t('callDetails.header.moreActions')}
              icon={EllipsisVertical}
              items={dropdownItems}
              menuOptions={{
                width: '20rem',
              }}
            />
          ) : (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText={t('callDetails.header.copyLinkToCall')}
              actionIcon={Link}
              onClick={() => handleCopyLink()}
            />
          )}
        </Flex>
      )}
      {isModal && (
        <Flex
          data-testid="call-logs-detail-view-header-quickactions"
          alignItems="center"
          gap="0.75rem"
        >
          {hasAudio ? (
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText={t('callDetails.header.moreActions')}
              icon={EllipsisVertical}
              items={dropdownItems}
              menuOptions={{
                width: '20rem',
              }}
            />
          ) : (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText={t('callDetails.header.copyLinkToCall')}
              actionIcon={Link}
              onClick={() => handleCopyLink()}
            />
          )}
          <Close
            size={Size.sm}
            iconProps={{ boxSize: '0.63rem' }}
            onClick={onClosed}
          />
        </Flex>
      )}
    </Flex>
  )
}
